import {
  createFileRoute,
  useParams,
  useSearch,
  useNavigate,
  Link,
} from "@tanstack/react-router";
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth, SessionExpiredError } from "@/lib/auth-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type {
  RFQDto,
  CreateQuoteRequest,
} from "@procureflow/shared-types";

// US-020 — Record vendor quote. AUTO-Q4 dedicated route at
// /rfq/$id/quotes/new?vendorId=<id>. Posts CreateQuoteRequest to
// /api/rfqs/:rfqId/quotes (MSW handler at apps/web/src/mocks/handlers/
// rfqs.ts:412-516; BE shipped in M3.B). Renders 400 envelopes inline so
// DEADLINE_PASSED / DUPLICATE / VALIDATION_ERROR all surface to the user.

export const Route = createFileRoute("/rfq/$id/quotes/new")({
  validateSearch: (search: Record<string, unknown>) => ({
    vendorId:
      typeof search.vendorId === "string" ? search.vendorId : undefined,
  }),
  head: () => ({
    meta: [{ title: "Record Quote — ProcureFlow" }],
  }),
  component: RecordQuotePage,
});

function RecordQuotePage() {
  const { id: rfqId } = useParams({ from: "/rfq/$id/quotes/new" });
  const { vendorId } = useSearch({ from: "/rfq/$id/quotes/new" });
  const { user } = useAuth();
  const navigate = useNavigate();
  const allowed = user?.role === "ProcurementOfficer";

  const [rfq, setRfq] = useState<RFQDto | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  // rfq_line_item_id -> unit_price string (parsed at submit time).
  const [unitPrices, setUnitPrices] = useState<Record<string, string>>({});
  const [leadTimeDays, setLeadTimeDays] = useState<string>("");
  const [paymentTerms, setPaymentTerms] = useState<string>("");
  const [deliveryTerms, setDeliveryTerms] = useState<string>("");
  const [validityPeriod, setValidityPeriod] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    async function load() {
      try {
        // fetchWithAuth awaits mswReady internally per ADR-016 Wave D.
        const res = await fetchWithAuth(`/api/rfqs/${rfqId}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setLoadError(body?.message ?? "Failed to load RFQ.");
          return;
        }
        const { data } = (await res.json()) as { data: RFQDto };
        if (!cancelled) setRfq(data);
      } catch (err) {
        if (err instanceof SessionExpiredError) return;
        if (!cancelled) setLoadError("An unexpected error occurred.");
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [rfqId, allowed]);

  const vendor = useMemo(
    () => rfq?.vendors.find((v) => v.vendor_id === vendorId) ?? null,
    [rfq, vendorId],
  );

  const totalCents = useMemo(() => {
    if (!rfq) return 0;
    return rfq.line_items.reduce((sum, li) => {
      const raw = unitPrices[li.id];
      const parsed = raw === undefined || raw === "" ? 0 : Number(raw);
      const price = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
      return sum + li.quantity * Math.floor(price);
    }, 0);
  }, [rfq, unitPrices]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!rfq || !vendorId) return;

    // Client-side parity with BE rules so inline error rendering is
    // immediate without a network round-trip on obvious mistakes.
    for (const li of rfq.line_items) {
      const raw = unitPrices[li.id];
      const n = Number(raw);
      if (raw === undefined || raw === "" || !Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
        setFormError(
          "Each unit price must be a whole number of cents, zero or more.",
        );
        return;
      }
    }

    const body: CreateQuoteRequest = {
      vendor_id: vendorId,
      lead_time_days: leadTimeDays ? Number(leadTimeDays) : undefined,
      payment_terms: paymentTerms || undefined,
      delivery_terms: deliveryTerms || undefined,
      validity_period: validityPeriod || undefined,
      line_items: rfq.line_items.map((li) => ({
        rfq_line_item_id: li.id,
        unit_price: Number(unitPrices[li.id] ?? "0"),
      })),
    };

    setSubmitting(true);
    try {
      const res = await fetchWithAuth(`/api/rfqs/${rfqId}/quotes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.status !== 201) {
        const errJson = await res.json().catch(() => ({}));
        setFormError(errJson?.message ?? "Failed to record quote.");
        return;
      }
      navigate({ to: "/rfq/$id", params: { id: rfqId } });
    } catch (err) {
      if (err instanceof SessionExpiredError) return;
      setFormError("An unexpected error occurred.");
    } finally {
      setSubmitting(false);
    }
  }

  if (!allowed) {
    return (
      <div className="space-y-4">
        <Link
          to="/rfq"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> RFQs
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
          <p className="text-sm font-medium text-foreground">Access denied</p>
          <p className="text-sm text-muted-foreground">
            You do not have access to record quotes.
          </p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <Link
          to="/rfq"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> RFQs
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
          <p className="text-sm text-destructive">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!rfq) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="space-y-4">
        <Link
          to="/rfq/$id"
          params={{ id: rfqId }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to RFQ
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
          <p className="text-sm font-medium text-foreground">Vendor not on this RFQ</p>
        </div>
      </div>
    );
  }

  return (
    <form className="space-y-5" onSubmit={handleSubmit}>
      <Link
        to="/rfq/$id"
        params={{ id: rfqId }}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to RFQ
      </Link>

      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 space-y-2">
        <h1 className="text-lg font-semibold">
          Record quote — {vendor.company_name}
        </h1>
        <p className="text-sm text-muted-foreground">
          RFQ <span className="font-mono">{rfq.rfq_number}</span>
        </p>
      </div>

      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-sm font-semibold">Line items</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Description</TableHead>
              <TableHead className="w-20">Qty</TableHead>
              <TableHead className="w-32">UoM</TableHead>
              <TableHead className="w-40">Unit price (cents)</TableHead>
              <TableHead className="w-40 text-right">Line total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rfq.line_items.map((li) => {
              const raw = unitPrices[li.id] ?? "";
              const parsed = raw === "" ? 0 : Number(raw);
              const lineTotal =
                Number.isFinite(parsed) && parsed >= 0
                  ? li.quantity * Math.floor(parsed)
                  : 0;
              return (
                <TableRow key={li.id}>
                  <TableCell>{li.description}</TableCell>
                  <TableCell className="tabular-nums">{li.quantity}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {li.unit_of_measure}
                  </TableCell>
                  <TableCell>
                    <Input
                      aria-label={`Unit price for ${li.description}`}
                      type="number"
                      min={0}
                      step={1}
                      value={raw}
                      onChange={(e) =>
                        setUnitPrices((prev) => ({
                          ...prev,
                          [li.id]: e.target.value,
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {lineTotal}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <div className="px-5 py-3 border-t border-border text-right text-sm">
          Total:{" "}
          <span data-testid="quote-total" className="font-medium tabular-nums">
            {totalCents}
          </span>{" "}
          cents
        </div>
      </div>

      <div className="rounded-2xl bg-card border border-border shadow-elegant p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="lead-time-days">Lead time (days)</Label>
          <Input
            id="lead-time-days"
            type="number"
            min={0}
            step={1}
            value={leadTimeDays}
            onChange={(e) => setLeadTimeDays(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="payment-terms">Payment terms</Label>
          <Input
            id="payment-terms"
            value={paymentTerms}
            onChange={(e) => setPaymentTerms(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="delivery-terms">Delivery terms</Label>
          <Input
            id="delivery-terms"
            value={deliveryTerms}
            onChange={(e) => setDeliveryTerms(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="validity-period">Validity period</Label>
          <Input
            id="validity-period"
            value={validityPeriod}
            onChange={(e) => setValidityPeriod(e.target.value)}
          />
        </div>
      </div>

      {formError && (
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {formError}
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={submitting}>
          {submitting ? "Saving…" : "Save quote"}
        </Button>
      </div>
    </form>
  );
}
